import { useEffect, useRef, useState } from "react";
import type { SimulationPhase } from "../engine/simulateProject.ts";
import type {
  SimulateProjectWorkerRequest,
  SimulationWorkerMessage,
} from "../engine/simulateProject.worker.ts";
import type { SimulationResults } from "../model/results/simulationResults.ts";

/**
 * The subset of the real `Worker` API this hook touches — narrowed so a
 * test can inject a plain mock object instead of a real browser `Worker`
 * (constructing one needs a `Worker` global and a bundler's own
 * `new URL(..., import.meta.url)` resolution, neither available under
 * vitest's default test environment).
 */
export interface SimulationWorkerLike {
  postMessage(message: SimulateProjectWorkerRequest): void;
  terminate(): void;
  onmessage: ((e: MessageEvent<SimulationWorkerMessage>) => void) | null;
  onerror: ((e: ErrorEvent) => void) | null;
}

interface UseSimulationWorkerOptions {
  /** Builds a fresh worker for one run — production (App.tsx) passes `() => new Worker(new URL("./engine/simulateProject.worker.ts", import.meta.url), { type: "module" })`; a test passes a `SimulationWorkerLike` mock instead. Called exactly once per `start()` call. */
  createWorker: () => SimulationWorkerLike;
  /** Shown as `simulationError` when the worker crashes OUTSIDE `simulateProject()`'s own try/catch (a module load failure, an out-of-memory kill) — see `start`'s `onerror` for why this can't reuse the worker's own reported message. */
  workerCrashedMessage: string;
}

interface UseSimulationWorkerResult {
  /** True while a run's Worker is in flight — drives the Run Simulation button's spinner/disabled state. */
  simulating: boolean;
  /** The worker's latest progress message, `null` while idle or before the first one arrives. */
  simulationProgress: { phase: SimulationPhase; fraction: number } | null;
  simulationError: string | null;
  results: SimulationResults | null;
  /**
   * Starts a new run, terminating any still-running one first.
   * `rawSections`/`externalFiles` are cloned into plain `Map`s here — the
   * structured-clone-friendly shape `postMessage` needs, regardless of what
   * kind of `Map`/`ReadonlyMap` the caller's project state holds.
   */
  start: (input: {
    rawSections: ReadonlyMap<string, string[][]>;
    externalFiles?: ReadonlyMap<string, string>;
  }) => void;
  /** Project-switch case (opening/closing/duplicating/deleting mid-run) — terminates the worker AND clears `results`, since a stale result must never survive under a DIFFERENT project's element names. */
  stop: () => void;
  /** The "Cancel" action while `simulating` — terminates the worker but leaves `results` alone, so a previous successful run stays visible. */
  cancel: () => void;
  /** Clears `results` alone — the in-place-edit case (`mutateRaw`/`undo`/`redo`): a project edit invalidates any previously-completed result but does NOT cancel a simulation still running for the pre-edit network. */
  clearResults: () => void;
  /** Clears `simulationError` alone — dismissing the "failed" dialog has nothing to do with the worker, which has already finished (with an error) by the time the dialog can show. */
  dismissError: () => void;
}

/**
 * `Worker.terminate()` kills the worker THREAD, but a `postMessage` it
 * already sent (most commonly its own final "done" result) can already be
 * sitting in the main thread's event queue at the moment `terminate()` runs,
 * and still gets DELIVERED afterward — `terminate()` does not retroactively
 * cancel an already-queued message. Every termination path below clears
 * `onmessage`/`onerror` FIRST, so an already-queued message for that worker
 * instance finds no handler left to invoke (a listener is looked up at
 * DISPATCH time, not bound at queue time). `start`'s `onmessage`/`onerror`
 * closures also check `workerRef.current === worker` before applying
 * anything, as a second, independent guard against a stale result from a
 * terminated (or superseded) run overwriting state for a newer one.
 */
export function useSimulationWorker(opts: UseSimulationWorkerOptions): UseSimulationWorkerResult {
  const [simulating, setSimulating] = useState(false);
  const [simulationProgress, setSimulationProgress] = useState<{
    phase: SimulationPhase;
    fraction: number;
  } | null>(null);
  const [simulationError, setSimulationError] = useState<string | null>(null);
  const [results, setResults] = useState<SimulationResults | null>(null);
  const workerRef = useRef<SimulationWorkerLike | null>(null);

  function terminate(worker: SimulationWorkerLike | null): void {
    if (!worker) return;
    worker.onmessage = null;
    worker.onerror = null;
    worker.terminate();
  }

  function stop(): void {
    terminate(workerRef.current);
    workerRef.current = null;
    setSimulating(false);
    setSimulationProgress(null);
    setSimulationError(null);
    setResults(null);
  }

  function cancel(): void {
    terminate(workerRef.current);
    workerRef.current = null;
    setSimulating(false);
    setSimulationProgress(null);
    setSimulationError(null);
  }

  function clearResults(): void {
    setResults(null);
  }

  function dismissError(): void {
    setSimulationError(null);
  }

  function start(input: {
    rawSections: ReadonlyMap<string, string[][]>;
    externalFiles?: ReadonlyMap<string, string>;
  }): void {
    terminate(workerRef.current);
    const worker = opts.createWorker();
    workerRef.current = worker;
    setSimulating(true);
    setSimulationProgress(null);
    setSimulationError(null);
    worker.onmessage = (e) => {
      // Stale message from a worker already replaced/terminated — see this
      // file's header.
      if (workerRef.current !== worker) return;
      if (e.data.type === "progress") {
        setSimulationProgress({ phase: e.data.phase, fraction: e.data.fraction });
        return;
      }
      if (e.data.ok) setResults(e.data.results);
      else setSimulationError(e.data.message);
      setSimulating(false);
      setSimulationProgress(null);
      terminate(worker);
      workerRef.current = null;
    };
    worker.onerror = (e) => {
      // Same stale-worker guard as onmessage above.
      if (workerRef.current !== worker) return;
      // simulateProject() already catches and reports its own errors via
      // onmessage — this only fires for something outside that (a module
      // load failure, an out-of-memory kill), where `e.message` is
      // frequently empty/redacted by the browser.
      console.error("Simulation worker crashed:", e.message);
      setSimulationError(opts.workerCrashedMessage);
      setSimulating(false);
      setSimulationProgress(null);
      terminate(worker);
      workerRef.current = null;
    };
    const request: SimulateProjectWorkerRequest = {
      rawSections: new Map(input.rawSections),
      externalFiles: input.externalFiles ? new Map(input.externalFiles) : undefined,
    };
    worker.postMessage(request);
  }

  // Defensive cleanup for unmount — every project-switch path already calls stop() itself.
  useEffect(() => {
    return () => terminate(workerRef.current);
  }, []);

  return {
    simulating,
    simulationProgress,
    simulationError,
    results,
    start,
    stop,
    cancel,
    clearResults,
    dismissError,
  };
}
